// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { breadcrumbsMenuStyle } from "@gcg/XDL/PreStyle/breadcrumbs";
import { newGuid } from "@insite/client-framework/Common/StringHelpers";
import { BreadcrumbsProps } from "@insite/mobius/Breadcrumbs";
import Clickable from "@insite/mobius/Clickable/Clickable";
import Link from "@insite/mobius/Link";
import Menu from "@insite/mobius/Menu/Menu";
import Typography from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as React from "react";
import styled, { css, withTheme } from "styled-components";

// eslint-disable-next-line spire/fenced-imports
import mixpanel from "mixpanel-browser";

export const Slash = styled(Typography as any).attrs({ children: ">" })`
    margin: 0 10px;
    color: ${getColor("secondary")};
`;

export const StyledNav = styled.nav.attrs({ "aria-label": "breadcrumbs" })`
    display: flex;
    flex-direction: row;
    align-items: center;
    ${injectCss}
`;

const reformatLinks = (links: []) => {
    return links.map((item: any) => {
        return {
            id: newGuid(),
            title: item.children,
            url: item.href,
        };
    });
};
export type BreadcrumbsPresentationProps = Omit<BreadcrumbsProps, "links">;

type props = { hideCurrentPage?: boolean } & BreadcrumbsProps;

const Breadcrumbs: React.FC<props> = withTheme(({ hideCurrentPage, links, ...otherProps }) => {
    const { applyProp, spreadProps } = applyPropBuilder(otherProps, { component: "breadcrumbs" });
    const typographyProps = spreadProps("typographyProps");
    const minOverFlowItems = 5;

    const handleMpEvent = (link: any) => {
        // eslint-disable-next-line no-prototype-builtins
        if ((window as any).hasOwnProperty("mixpanelInitialized")) {
            mixpanel.track("Breadcrumbs", {
                "Origin URL": window.location.pathname,
                "Destination URL": link.href,
            });
        }
    };

    // return null if `links` is an empty empty object, collection, map, or set.
    if (!links || links.length === 0) {
        return null;
    }

    // Remove last item out of the breadcrumb links if hideCurrentPage is true
    if (hideCurrentPage) {
        links = [...links];
        links.pop();
    }

    let children = [];
    if (links.length > minOverFlowItems) {
        const shortLinks = [...links.slice(0, 2), links[links.length - 1]];
        const menuLinks = reformatLinks(links);
        const popupLinks = [...menuLinks.slice(3, menuLinks.length - 1)];
        children = shortLinks.map((link: any, index: number) => {
            const isLastItem = index === links.length - 1;
            const children = link.children;
            // yield null if no children
            if (!React.Children.count(children)) {
                return null;
            }
            const key = typeof children === "string" ? `${children}_${index}` : index;
            if (index === 2) {
                const menuKey = `${key}menu`;
                return (
                    <React.Fragment key={key}>
                        <Menu
                            descriptionId="breadcrumbsMenu"
                            menuItems={popupLinks ?? []}
                            maxDepth={1}
                            menuTrigger={
                                <Clickable data-test-selector="breadcrumbs_menu">
                                    <Typography {...typographyProps} aria-current="page" key={menuKey}>
                                        ...
                                    </Typography>
                                </Clickable>
                            }
                            cssOverrides={breadcrumbsMenuStyle.cssOverrides}
                        />
                        <Slash {...typographyProps} />
                        <Typography
                            data-test-selector={`breadCrumb_${children}`}
                            {...typographyProps}
                            aria-current="page"
                            key={key}
                        >
                            {children}
                        </Typography>
                    </React.Fragment>
                );
            }
            if (isLastItem && !hideCurrentPage) {
                return (
                    <Typography {...typographyProps} aria-current="page" key={key}>
                        {children}
                    </Typography>
                );
            }
            return (
                <React.Fragment key={key}>
                    <Link
                        color="text.main"
                        typographyProps={typographyProps}
                        {...link}
                        onClick={() => handleMpEvent(link)}
                    />
                    {!isLastItem && <Slash {...typographyProps} />}
                </React.Fragment>
            );
        });
    } else {
        children = links.map((link: any, index: number) => {
            const isLastItem = index === links.length - 1;
            const children = link.children;
            // yield null if no children
            if (!React.Children.count(children)) {
                return null;
            }
            const key = typeof children === "string" ? children : index;
            if (isLastItem && !hideCurrentPage) {
                return (
                    <Typography {...typographyProps} aria-current="page" key={key}>
                        {children}
                    </Typography>
                );
            }
            return (
                <React.Fragment key={key}>
                    <Link
                        color="text.main"
                        typographyProps={typographyProps}
                        {...link}
                        onClick={() => handleMpEvent(link)}
                    />
                    {!isLastItem && <Slash {...typographyProps} />}
                </React.Fragment>
            );
        });
    }
    // return null if `links` map yields an array of nulls
    if (Object.keys(children.filter((x: typeof Link) => x !== null)).length === 0) {
        return null;
    }

    return (
        <StyledNav {...otherProps} css={applyProp("css")}>
            {children}
        </StyledNav>
    );
});

Breadcrumbs.defaultProps = {
    links: [],
};

Breadcrumbs.displayName = "Breadcrumbs";
/** @component */
export default Breadcrumbs;
